import type { TableField } from "bootstrap-vue-next/dist/src/types";
import type { ButtonVariant } from "bootstrap-vue-next";
import type { Decimal } from "decimal.js-light";

export type CallbackFn<T, V, R> = (item: TableItem<T, V>) => R;
export type OnChangeFn<T, V> = (item: TableItem<T, V>, row: string | number | undefined) => void;

export type showTotalFn = () => number | string;

export type TableFieldParam<T, V> = TableField & {
  key: string;
  editable?: {
    type: "number" | "text" | "url" | "email" | "checkbox";
    step?: number | CallbackFn<T, V, number>;
    min?: number;
    onChange?: OnChangeFn<T, V>;
  };
  validation?: CallbackFn<T, V, boolean | string>;
  setVariant?: CallbackFn<T, V, string>;
  enabled?: boolean | CallbackFn<T, V, boolean>;
  disabledText?: string | CallbackFn<T, V, string>;
  helpText?: string | CallbackFn<T, V, string>;
  cssClassHelpText?: string | CallbackFn<T, V, string>;
  button?: {
    disabled?: boolean | CallbackFn<T, V, boolean>;
    text?: string;
    icon?: string;
    variant?: ButtonVariant;
    onClick: OnChangeFn<T, V>;
  };
  calculate?: CallbackFn<T, V, V>;
  showTotal?: boolean | showTotalFn;
  showTotalHelpText?: () => string;
  showTotalCssClass?: () => string[];
  fieldFormatter?: (value: string | number | Decimal | null, key: string, item: T | undefined) => string;
  hideable?: boolean;
  placeholder?: string;
};

export interface TableItem<T, V> {
  item?: T;
  key: number;
  index: number;
  variant?: string;
  field: TableFieldParam<T, V>;
  items: T[];
  value: V | string;
  detailsShowing: boolean;
  rowSelected: boolean;
}

export interface Articolo {
  /**
   * identificativo univoco articolo
   */
  artkey: string;

  /**
   * Codice articolo
   */
  veccdart: string;

  /**
   * Descrizione articolo
   */
  vecdsart: string;

  /**
   * Prezzo pezzi
   */
  vecpzpez: number;

  /**
   * Prezzo unità
   */
  vecpzuni: number;

  /**
   * Prezzo listino
   */
  vecpzunilis: number;

  /**
   * Disponibilità
   */
  vecqtedi: number;

  /**
   * Disponibilità confezioni
   */
  vecqtediconf: number;

  /**
   * Valore ultima vendita
   */
  vecqtuven: number;



  /**
   * Flag che indica se il prodotto è in esaurimento
   */
  vecswinesaurimento: boolean;

  /**
   * Lotto
   */
  veccdlotto: string;

  /**
   * Qta per Bancale
   */
  vecnmcasped: number;

  /**
   * Pezzi per confezione
   */
  vecnmpez: number;

  /**
   * Unità di misura
   */
  veccdumi: string;

  /**
   * Prezzo di acquisto
   */
  vecpzacquisto: number;

  /**
   * Flag abilitazione pubblicità
   */
  vecswmatpubb: boolean;

  /**
   * Flag abilitazione omaggio
   */
  vecswmatomag: boolean;

  /**
   * Flag abilitazione vendita
   */
  vecswmatvend: boolean;

  vecqtvenduto: number;

  vecvlaccisa: number;

  vecvlscqta: number;

  vecpzultven: number;
  vecdtultven: number;
  vecvlscoqta: number;
  vecscri1: number;
  vecscri2: number;
  vecscri3: number;
  vecscri4: number;
  vecscri5: number;
  veccdlottopro: string;
  /**
   * descrizione promozione
   */
  vecdscanvas: string;
}

export interface OrdineRiga {
  /*
   * identificativo univoco articolo generato da backend
   */
  artkey: string;

  /**
   * Campo Confezioni in Ordine in Input
   */
  i_qta_ord: number;

  /**
   * Campo Confezioni Omaggio in input
   */
  i_qta_omag: number;

  /**
   * Campo pezzi Omaggio in input
   */
  i_pezzi_omag: number;

  /**
   *  Campo Confezioni Pubblicità in Input
   */
  i_qta_pubb: number;

  /**
   * Campo pezzi Pubblicità in input
   */
  i_pezzi_pub: number;

  /**
   * Campo Quantità in input
   */
  i_qta_pezzi: number;

  /**
   * Quantità riga ordine espressa in bancali ( usata solo come helper durante l'input )
   */
  i_qta_banc?: number;

  /**
   * sconto 1
   */
  i_vecscri1: number;

  /**
   * sconto 2
   */
  i_vecscri2: number;

  /**
   * sconto 3
   */
  i_vecscri3: number;
  /**
   * sconto 4
   */
  i_vecscri4: number;

  /**
   * sconto 5
   */
  i_vecscri5: number;

  /**
   * percentuale iva
   */
  vecpcali: number;

  errors?: { [key: string]: string };
}

export interface Cliente {
  /**
   * creato da webapp - indirizzo completo del cliente
   */
  indirizzo?: string;

  /**
   * creato da webapp - singolo valore chiave univoco ( codice cliente + codice destinazione )
   */
  key?: string;

  /**
   * @key
   *  codice cliente
   */
  veccdclf: string;

  /**
   * ragione sociale cliente o destinazione
   */
  vecdsrso: string;

  /**
   * indirizzo cliente
   */
  vecdsind: string;

  /**
   * cap cliente
   */
  veccdcap: string;

  /**
   * provincia cliente
   */
  veccdpro: string;

  /**
   * località cliente
   */
  vecdsloc: string;

  /**
   *  codice pagamento
   */
  veccdpag: string;

  /**
   * * codice listino
   */
  veccdlis: string;

  /**
   * * sconto 1
   */
  vecscri1: string;

  /**
   * * sconto 2
   */
  vecscri2: string;

  /**
   *  * sconto 3
   */
  vecscri3: string;

  /**
   *  * sconto 4
   */
  vecscri4: string;

  /**
   *  * sconto 5
   */
  vecscri5: string;

  /**
   * * se destinazione primaria
   */
  vecswprimario: boolean;

  /**
   * * coordinate gps longitudine  cliente
   */
  vecnmlongitud: number;

  /**
   * * coordinate gps longitudine  cliente
   */
  vecnmlatitud: number;

  /**
   * * codice grade cliente
   */
  veccdgrade: string;

  /**
   * telefono cliente
   */
  vecdstel: string;

  /**
   * cellulare cliente
   */
  vecdscel: string;

  /**
   * partita iva cliente
   */
  veccdpiv: string;

  /**
   * codice fiscale cliente
   */
  veccdfis: string;

  /**
   * Contatti cliente
   */
  vecdscontatti: string;

  /**
   * Indirizzo email cliente
   */
  vecdsemail: string;

  /**
   * Descirizione pagamento
   */
  vecdspag: string;

  /**
   * Zona
   */
  vecdszona: string;

  /**
   * Giorno consegna cliente
   */
  vecdsconsegna: string;

  /**
   * Giorno chiusura cliente
   */
  vecdschiusura: string;

  /**
   * Codice identificativo SDI
   */
  veccdsdi: string;

  /**
   * PEC SDI
   */
  vecpecsdi: string;

  /**
   * fido concesso cliente (non fido calcolato)
   */
  vecvlfido: string;

  /**
   * creato da webapp - indirizzo completo della destinazione
   */
  indirizzoDestinazione?: string;

  /**
   * @key
   * codice destinazione
   */
  veccddes: string;

  /**
   *  ragione sociale destinazione
   */
  DESvecdsrso: string;

  /**
   * indirizzo destinazione
   */
  DESvecdsind: string;

  /**
   * localita destinazione
   */
  DESvecdsloc: string;

  /**
   * cap destinazione
   */
  DESveccdcap: string;

  /**
   * codice provincia destinazione
   */
  DESveccdpro: string;

  /**
   * telefono destinazione
   */
  DESvecdstel: string;

  /**
   * cellulare destinazione
   */
  DESvecdscel: string;

  /**
   * Giorno consegna cliente
   */
  DESvecdsconsegna: string;

  /**
   * Giorno chiusura cliente
   */
  DESvecdschiusura: string;

  /**
   * ??
   */
  cli_vecdsrso?: string;

  veccdlottopro: string;

  Nota: string;
}

export interface Ordine {
  Testata: TestataOrdine;
  Dettaglio: OrdineArticolo[];

  idDoc: string;
}

export interface OrdineArticolo extends OrdineRiga, Articolo {
  aggiunto?: boolean;
}

export interface PartitaAperta {
  Progressivo: number;
  CodiceDoc: string;
  Numero: number;
  Data: string | Date;
  DataScadenza: string | Date;
  ImportScadenza: number;
  ImportoTrasmesso: number;
  i_vecvlincassato: number;
  i_assegno: boolean;
  Corrispettivo: number | boolean;
}

export interface IncassiAgente {
  Progressivo: number;
  CodiceDoc: string;
  Numero: number;
  Data: string | Date;
  DataScadenza: string | Date;
  ImportScadenza: number;
  Assegno: boolean;
  Abbuono: boolean;
  ImportoIncassato: number;
  ImportAbbuono: number;
  Corrispettivo: number;
  Cliente: string;
  DataRegistrazione: string | Date;
}

export const ordineRigaKeys = [
  "artkey",
  "i_qta_omag",
  "i_qta_ord",
  "i_qta_pubb",
  "i_qta_pezzi",
  "i_vecscri1",
  "i_vecscri2",
  "i_vecscri3",
  "i_vecscri4",
  "i_vecscri5",
  "errors",
];
/*
export const articoloKeys = [
  "artkey",
  "veccdart",
  "veccdlotto",
  "vecdsart",
  "vecpzpez",
  "vecpzuni",
  "vecpzunilis",
  "vecqtedi",
  "vecqtuven",
  "vecswmatpubb",
  "vecswmatvend",
  "vecswmatomag",
  "vecswinesaurimento",
  "vecnmcasped",
  "vecnmpez",
  "veccdumi",
  "vecpzacquisto",
  "vecpzultven",
  "vecdtultven",
  "vecqtediconf",
  "vecscri1",
  "vecscri2",
  "vecscri3",
  "vecscri4",
  "vecscri5",
  "veccdlottopro",
  "vecqtvenduto",
  "vecvlaccisa",
  "vecvlscoqta"
];
*/
export type RowItem<T> = /* { [P in keyof T]: T[P] } */ T & {
  _cellVariants?: { [key: string]: string | null | undefined };
  _rowVariant?: string;
};

export interface TestataOrdine {
  /**
   * Messaggio che indica il tipo di blocco
   */
  vecdsmsgblocco: string;

  /**
   * Blocco su ORDINI di vendita Standard
   */
  vecswocllocked: boolean;

  /**
   * Valore fido
   */
  vecvlfido: number;

  /**
   * Totale accise
   */
  vecvlaccisa: number;

  /**
   * Totale imponibile (senza iva)
   */
  vecvlrig: number;

  /**
   * Totale con iva
   */
  vecvlrii: number;

  /**
   * Totale pezzi
   */
  vecqtrig: number;

  /**
   * Totale confezioni
   */
  vecqtcas: number;

  /**
   * Totale Cauzioni
   */
  vecvlcau: number;

  /**
   * Totale referenze
   */
  vecnmrighe: number;

  /**
   * Totale documento
   */
  vecvldoc: number;

  /**
   * Confezioni in omaggio
   */
  vecqtcao: number;

  /**
   *  Pezzi singoli in omaggio
   */
  vecqtrigo: number;

  /**
   * Confezioni di Pubblicità
   */
  vecqtcap: number;

  /**
   * Pezzi singoli in Pubblità
   */
  vecqtrigp: number;

  /**
   * Nota dell'ordine
   */
  i_vecntmem: string;

  /**
   * Ordine per corrispettivo
   */
  i_vecswcorr: boolean;

  /**
   * ??
   */
  i_vecswprenot: boolean;

  /**
   * indirizzo email per invio documento
   */
  i_vecdsemail: string;

  /**
   * Firma grafometrica
   */
  i_sFirma: string;

  /**
   * Corrispettivo disabilitato globalmente
   */
  vecswenablecorr: boolean;

  /**
   * Corrispettivo disabilitato per questo ordine
   */
  vecswocplocked: boolean;
}

export interface Impostazioni {
  IdDoc: string;
  ColonneVisibiliOrdine: string[];
  ControllaGiacenzaOrdine: boolean;
  Label: string;
  AbilitaTastoCorrispettivo: boolean;
  NotaDiDefault: string;
  RichiediFirma: boolean;
  ControlloFido: boolean;
  ConsentiSfusi: boolean;
  FormulaSconti?: string;
  FormulaScontiUnitario?: string;
}

export interface TestateStorico {
  Progressivo: number;
  Cliente: string;
  CodiceCliente: string;
  Numero: string;
  Data: string | Date;
  TipoDoc: string;
  Stato: string;
  Anteprima: boolean;
  CodiceDocumento: string;
  AccessToken: string;
  Nota: string;
  CodiceDestinazione: string;
  DestinazioneMerce: string;
  TotaleImponibile: number;
  TipoStampa: string;
}

export interface ElencoClientiPerStorico {
  veccdclf: string;
  vecdsrso: string;
  vecdsdes: string;
  veccddes: string;
}

export interface DettaglioStorico {
  Movimento: string;
  Codice: string;
  Lotto: string;
  Descrizione: string;
  Qta: number;
  UM: string;
  PzAge: number;
  PzUni: number;
  ValoreRiga: number;
}

export interface IsalesTableConfig {
  id: number; // bigint NOT NULL IDENTITY (1, 1),
  order_type: string; // nchar(1) NOT NULL,
  i_field_position: number; // int NOT NULL,
  i_field_type: string; // nvarchar(25) NOT NULL CHECK (field_type IN('float', 'integer', 'text', 'long-text', 'date', 'currency', 'bancali', 'confezioni', 'omaggio', 'pubblicita', 'pezzi', 'editable', 'percentage')),
  i_field_key: string; // nvarchar(50) NOT NULL,
  i_field_label: string; // nvarchar(150) NOT NULL,
  i_field_sortable: boolean; // bit NOT NULL,
  i_field_sticky: boolean; // bit NOT NULL,
  i_field_header: boolean; // bit NOT NULL,
  i_field_hideable: boolean; // bit NOT NULL,
  i_field_placeholder: string | null; // nvarchar(250) NULL,
  i_field_emoji: string | null; // nvarchar(5) NULL
}

export interface SalesStatsVendite {
  ClasseArticolo: string;
  veccdart: string;  // Codice articolo
  vecdsart: string;  // Descrizione articolo
  FatturatoPeriodoC: number;  // Fatturato periodo corrente
  VendutoPeriodoC: number;  // Venduto periodo corrente
  FatturatoPeriodoP: number;  // Fatturato periodo Anno Precedente
  VendutoPeriodoP: number;  // Venduto periodo Anno Precedente
  PercFatturatoPeriodo: number;  // Raffronto % PC\PP
  FatturatoAnnoC: number;  // Fatturato anno corrente
  VendutoAnnoC: number;  // Venduto anno corrente
  FatturatoAnnoP: number;  // Fatturato anno precedente
  VendutoAnnoP: number;  // Venduto anno precedente
  PercFatturatoAnno: number;  // Raffronto % AC\AP
}

export interface SalesStats {
  label: string;
  sales: number;
  d_start: number;
  d_end: number;
  date_start: string;
  date_end: string;
}

export interface SalesStatsDetail {
  Total: number;
  Details: TestateStorico[];
  DateFrom: string;
  DateTo: string;
}
